import { Badge } from "@mui/material";
import {
  FilterPanelTrigger,
  type GridColDef,
  GridFilterInputSingleSelect,
  GridFilterListIcon,
  type GridFilterOperator,
  Toolbar,
  getGridNumericOperators,
  gridClasses,
} from "@mui/x-data-grid-premium";
import React, { useContext } from "react";
import { ProjectContext } from "../../contexts/ProjectContext.tsx";
import BaseTable from "../datagrid/BaseTable.tsx";
import { TableToolbarButton } from "../datagrid/TableToolbarButton";
import PatientListItem from "./PatientListItem.tsx";

function CustomToolBar() {
  return (
    <Toolbar>
      <FilterPanelTrigger
        render={(props, state) => (
          <TableToolbarButton
            variant={"outlined"}
            fullWidth
            onClick={props.onClick}
            startIcon={
              <Badge
                badgeContent={state.filterCount}
                color="primary"
                variant="dot"
              >
                <GridFilterListIcon />
              </Badge>
            }
          >
            Filter Patients
          </TableToolbarButton>
        )}
      />
    </Toolbar>
  );
}

const isIncludedFilterOperator: GridFilterOperator<
  InternalPatientSummary,
  boolean
>[] = [
  {
    label: "Is",
    value: "is",
    getApplyFilterFn: (filterItem) => {
      if (!filterItem.value) return null;
      return (value) => {
        switch (filterItem.value) {
          case "Included":
            return value !== null && value;
          case "Excluded":
            return value !== null && !value;
          case "Indeterminate":
          default:
            return value === null;
        }
      };
    },
    InputComponent: GridFilterInputSingleSelect,
  },
];

const categoryFilterOperator: GridFilterOperator<
  InternalPatientSummary,
  boolean
>[] = [
  {
    label: "Is",
    value: "is",
    getApplyFilterFn: (filterItem) => {
      if (!filterItem.value) return null;
      return (_value, row: InternalPatientSummary) => {
        switch (filterItem.value) {
          case "Adverse Event":
            return row.adverseEvents;
          case "Clinical Benefit":
            return row.clinicalBenefit;
          case "Performance":
            return row.performance;
          default:
            return false;
        }
      };
    },
    InputComponent: GridFilterInputSingleSelect,
  },
];

const isNewFilterOperators: GridFilterOperator<
  InternalPatientSummary,
  boolean
>[] = [
  {
    label: "is",
    value: "is",
    getApplyFilterFn: (filterItem) => {
      if (!filterItem.value) return null;
      return (value) => {
        return filterItem.value === "New"
          ? value !== undefined && value
          : value === undefined || !value;
      };
    },
    InputComponent: GridFilterInputSingleSelect,
  },
];

function renderRow(patient: InternalPatientSummary) {
  return <PatientListItem index={patient.id} patientSummary={patient} />;
}

export default function PatientList({
  patientSelected,
  onPatientSelected,
}: {
  patientSelected: PatientContextType;
  onPatientSelected: (patientSummary: InternalPatientSummary) => void;
}) {
  const { highlightDate, patientSummaries } = useContext(ProjectContext) || {};

  const columns: GridColDef[] = [
    {
      field: "isNew",
      headerName: "Patient",
      editable: false,
      width: 285,
      renderCell: (params) => {
        return renderRow(params.row);
      },
      filterOperators: isNewFilterOperators,
      valueOptions: ["New", "Not New"],
      type: "singleSelect",
      filterable: !!highlightDate,
    },
    {
      field: "isIncluded",
      headerName: "Inclusion",
      width: 0,
      filterOperators: isIncludedFilterOperator,
      valueOptions: ["Included", "Excluded", "Indeterminate"],
      type: "singleSelect",
    },
    {
      field: "category",
      headerName: "Category",
      width: 0,
      filterOperators: categoryFilterOperator,
      valueOptions: ["Adverse Event", "Clinical Benefit", "Performance"],
      type: "singleSelect",
    },
    {
      field: "patientNumber",
      headerName: "Number",
      width: 0,
      type: "number",
      filterOperators: getGridNumericOperators().filter(
        (op) => op.value !== "isEmpty" && op.value !== "isNotEmpty",
      ),
    },
  ];

  return (
    <BaseTable<InternalPatientSummary>
      boxStyling={{
        display: "flex",
        flexDirection: "column",
        width: "300px",
        height: "100%",
      }}
      sx={{
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
          outline: "none",
        },
      }}
      loading={!patientSummaries || patientSummaries.length === 0}
      hideFooter={true}
      slots={{
        toolbar: CustomToolBar,
        columnHeaders: () => null,
      }}
      disableColumnSelector
      columnHeaderHeight={0}
      columnVisibilityModel={{
        isIncluded: false,
        category: false,
        patientNumber: false,
      }}
      getRowHeight={() => 60}
      density={"standard"}
      getRowClassName={(params) =>
        params.row.isNew ? "MuiDataGrid-row--highlighted" : ""
      }
      rows={patientSummaries || []}
      disableMultipleRowSelection
      rowSelection={true}
      rowSelectionModel={{
        ids: new Set([patientSelected.id!]),
        type: "include",
      }}
      onRowSelectionModelChange={(rowSelectionModel) => {
        const id = [...rowSelectionModel.ids][0];
        if (id === undefined || !patientSummaries) return;
        onPatientSelected(
          patientSummaries.filter(
            (p) => p.id === [...rowSelectionModel.ids][0],
          )[0],
        );
      }}
      columns={columns}
      tableId="patient-table"
      pagination={false}
    />
  );
}
